import {
  POLICE_DATA_CATEGORIES,
  SAFETY_REPORT_SEVERITIES,
  type SafetyAppealDecision,
  type SafetyReportDetail,
  type SafetyReportSeverity,
  type SafetyVerdict,
} from "@wallyo/contracts/console/safety";
import type { FormEvent, ReactNode } from "react";
import { t } from "../../i18n";
import { Modal, WriteFailureNotice } from "../team/TeamDialogs";
import type { WriteFailure } from "../team/team-format";
import type { ReportActionFields, ReportActionKind } from "./report-actions";
import { severityName, verdictName } from "./report-format";

export type ReportDialogState = {
  kind: ReportActionKind;
  fields: ReportActionFields;
  problem: string | null;
  failure: WriteFailure | null;
  pending: boolean;
};

export type ReportActionDialogProps = {
  detail: SafetyReportDetail;
  dialog: ReportDialogState;
  onFields: (fields: Partial<ReportActionFields>) => void;
  onSubmit: () => void;
  onClose: () => void;
  onReload: () => void;
};

const VERDICTS: readonly Exclude<SafetyVerdict, "permanent_ban">[] = ["none", "warning", "temporary_suspension"];
const DECISIONS: readonly SafetyAppealDecision[] = ["upheld", "overturned"];

function Field({ id, label, hint, children }: { id: string; label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
      {hint ? <span className="meta">{hint}</span> : null}
    </div>
  );
}

/** One action on a report: what it does, its own fields, the reason, then send. */
export function ReportActionDialog({ detail, dialog, onFields, onSubmit, onClose, onReload }: ReportActionDialogProps) {
  const { kind, fields } = dialog;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit();
  };
  const text = (id: keyof ReportActionFields, label: string, hint?: string, multiline = false) => (
    <Field id={`report-${id}`} label={label} hint={hint}>
      {multiline ? (
        <textarea
          id={`report-${id}`}
          rows={3}
          value={fields[id] as string}
          onChange={(event) => onFields({ [id]: event.target.value })}
        />
      ) : (
        <input
          id={`report-${id}`}
          value={fields[id] as string}
          onChange={(event) => onFields({ [id]: event.target.value })}
        />
      )}
    </Field>
  );
  const told = (hint: string) => text("toldReason", t("reports.field.toldReason"), hint, true);

  return (
    <Modal title={t(`reports.dialog.${kind}.title` as const, { reference: detail.reference })} onClose={onClose}>
      <form className="modal-form" onSubmit={submit} noValidate data-dialog={kind}>
        <div className="modal-body">
          <p>{t(`reports.dialog.${kind}.body` as const, { name: detail.subject.name })}</p>
        </div>

        {kind === "evidence" ? (
          <>
            {text("body", t("reports.field.evidence"), undefined, true)}
            {text("evidenceUrl", t("reports.field.evidenceUrl"), t("reports.field.evidenceUrlHint"))}
          </>
        ) : null}

        {kind === "severity" ? (
          <Field id="report-severity-pick" label={t("reports.field.severity")}>
            <select
              id="report-severity-pick"
              value={fields.severity}
              onChange={(event) => onFields({ severity: event.target.value as SafetyReportSeverity })}
            >
              {SAFETY_REPORT_SEVERITIES.map((severity) => (
                <option key={severity} value={severity}>
                  {severityName(severity)}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        {kind === "verdict" ? (
          <>
            <Field id="report-verdict" label={t("reports.field.verdict")}>
              <select
                id="report-verdict"
                value={fields.verdict}
                onChange={(event) => onFields({ verdict: event.target.value as ReportActionFields["verdict"] })}
              >
                {VERDICTS.map((verdict) => (
                  <option key={verdict} value={verdict}>
                    {verdictName(verdict)}
                  </option>
                ))}
              </select>
            </Field>
            {fields.verdict === "temporary_suspension" ? (
              <Field id="report-until" label={t("reports.field.suspendedUntil")}>
                <input
                  id="report-until"
                  type="datetime-local"
                  value={fields.suspendedUntil}
                  onChange={(event) => onFields({ suspendedUntil: event.target.value })}
                />
              </Field>
            ) : null}
            {told(t("reports.field.toldHint"))}
          </>
        ) : null}

        {kind === "ban" ? told(t("reports.field.toldHint")) : null}

        {kind === "appeal" ? (
          <>
            <Field id="report-decision" label={t("reports.field.decision")}>
              <select
                id="report-decision"
                value={fields.decision}
                onChange={(event) => onFields({ decision: event.target.value as SafetyAppealDecision })}
              >
                {DECISIONS.map((decision) => (
                  <option key={decision} value={decision}>
                    {t(`reports.appeal.${decision}` as const)}
                  </option>
                ))}
              </select>
            </Field>
            {told(t("reports.field.toldHint"))}
          </>
        ) : null}

        {kind === "police" ? (
          <>
            {text("station", t("reports.field.station"))}
            {text("officerName", t("reports.field.officerName"))}
            {text("officerContact", t("reports.field.officerContact"))}
            {text("policeReference", t("reports.field.policeReference"))}
          </>
        ) : null}

        {kind === "share" ? (
          <>
            <Field id="report-referral" label={t("reports.field.referral")}>
              <select
                id="report-referral"
                value={fields.policeReferralId}
                onChange={(event) => onFields({ policeReferralId: event.target.value })}
              >
                {detail.policeReferrals.map((referral) => (
                  <option key={referral.policeReferralId} value={referral.policeReferralId}>
                    {referral.station} · {referral.officerName}
                  </option>
                ))}
              </select>
            </Field>
            <fieldset className="pick-list">
              <legend>{t("reports.field.categories")}</legend>
              {POLICE_DATA_CATEGORIES.map((category) => (
                <label key={category}>
                  <input
                    type="checkbox"
                    checked={fields.categories.includes(category)}
                    onChange={(event) =>
                      onFields({
                        categories: event.target.checked
                          ? [...fields.categories, category]
                          : fields.categories.filter((each) => each !== category),
                      })
                    }
                  />{" "}
                  {t(`reports.police.category.${category}` as const)}
                </label>
              ))}
            </fieldset>
            {text("description", t("reports.field.shareDescription"), t("reports.field.shareDescriptionHint"), true)}
          </>
        ) : null}

        <Field
          id="report-reason"
          label={kind === "evidence" ? t("reports.field.reasonOptional") : t("team.reason")}
          hint={t("team.reasonHint")}
        >
          <textarea
            id="report-reason"
            rows={2}
            value={fields.reason}
            onChange={(event) => onFields({ reason: event.target.value })}
          />
        </Field>

        {dialog.problem ? <p className="field-problem">{dialog.problem}</p> : null}
        {dialog.failure ? <WriteFailureNotice failure={dialog.failure} onReload={onReload} /> : null}
        <div className="btns modal-actions">
          <button className="btn quiet" type="button" onClick={onClose}>
            {t("team.cancel")}
          </button>
          <button
            className={`btn ${kind === "ban" || kind === "suspend" ? "danger" : "primary"}`}
            type="submit"
            disabled={dialog.pending}
          >
            {dialog.pending ? t("team.sending") : t(`reports.action.${kind}` as const)}
          </button>
        </div>
      </form>
    </Modal>
  );
}
